import type { ParseKeys, TFunction } from 'i18next'

import { statusFromWire } from '@/features/cases/api/caseWire'
import { formatMoney } from '@/lib/format'
import type { Notification, ParticipantRole } from '@/types/domain'

/* The bell's message codes, worded in the reader's language (story B5, QW-1
 * B3/B4).
 *
 * Every bell row since B5 carries a `code` — a key of the server's registry in
 * `backend/platform/outbox/inapp_codes.go` — and `params`, the VALUES its words
 * are filled from: status codes, a role key, an ISO date, a decimal amount with
 * its currency, a count, a display name. The server never sends a sentence to
 * translate; it sends the facts and this table says them, under
 * `common.notifications.messages.<code>` in both catalogues.
 *
 * Mirrored, not imported: the registry is Go. `notificationMessages.test.ts`
 * parses it and fails when a code, a param name or the data-detail flag here is
 * not what the server declares — in both directions — and when either catalogue
 * names a param the code does not carry.
 *
 * Worded at RENDER, never in `notificationFromWire`: the feed cache outlives a
 * language switch. Anything this cannot word — no code (a row written before
 * B5), a code the server added ahead of this build, a value it cannot name —
 * returns undefined, and the drawer falls back to the exact-title table
 * (`notificationTitles.ts`) and then to the stored English. A guessed word is
 * worse than the server's own sentence. */

/** How a code's second line is produced.
 *  - `worded`: from the catalogue's `detail`, filled from the params;
 *  - `data`: the stored detail as is — a chat excerpt, a route, a client's name,
 *    a note somebody typed (the registry's `DetailIsData`);
 *  - `none`: the code has no second line. */
export type MessageDetail = 'worded' | 'data' | 'none'

export interface NotificationMessage {
  /** Exactly the registry's param keys for the code. */
  params: readonly string[]
  detail: MessageDetail
}

/** What a param's VALUE is, which decides how it is put into words.
 *
 *  Keyed by the param's NAME, which is the registry's: a name means one kind of
 *  value across every code (`amount` is always a decimal string, `date` always
 *  ISO). The drift test requires a kind for every name the registry uses, so a
 *  new param is classified here before it can reach a screen raw. */
export type ParamKind =
  /** A case status wire code, worded through the case status catalogue. */
  | 'status'
  /** A participant role key, worded through `participantRole`. */
  | 'role'
  /** An ISO `YYYY-MM-DD` day, rendered in the reader's date form. */
  | 'date'
  /** A decimal integer string, selecting the plural form. */
  | 'count'
  /** A decimal amount string, rendered with the code's `currency`. */
  | 'money'
  /** The currency of the code's amounts — folded into each, never printed alone. */
  | 'currency'
  /** A value printed as is: a name, an address, a number, a label. */
  | 'text'
  /** As `text`, but may be "": the detail then takes its `_bare` form, which
   *  does without it. */
  | 'optionalText'

export const PARAM_KINDS: Readonly<Record<string, ParamKind>> = {
  from: 'status',
  to: 'status',
  status: 'status',
  role: 'role',
  date: 'date',
  count: 'count',
  amount: 'money',
  current: 'money',
  requested: 'money',
  currency: 'currency',
  author: 'text',
  email: 'text',
  revision: 'text',
  caseNumber: 'text',
  item: 'optionalText',
  reference: 'optionalText',
}

const MONEY_PARAMS = ['current', 'requested', 'currency'] as const

/** The registry, mirrored. One entry per code, in the registry's order. */
export const NOTIFICATION_MESSAGES: Readonly<Record<string, NotificationMessage>> = {
  // casefile
  'case.status.moved': { params: ['from', 'to'], detail: 'none' },
  'case.assigned': { params: [], detail: 'data' },
  'case.status-sent': { params: ['status', 'email'], detail: 'worded' },

  // collab (chat). The quote chat's stored detail is always "" — the message
  // body is not carried to the bell for a quote — so there is no second line.
  'case.message': { params: ['author'], detail: 'data' },
  'quote.message': { params: ['author'], detail: 'none' },

  // quote
  'quote.sent': { params: ['revision', 'count'], detail: 'worded' },
  'quote.answered.rejected': { params: ['date'], detail: 'worded' },
  'quote.participant-added': { params: ['role'], detail: 'worded' },
  'quote.team-assigned': { params: [], detail: 'data' },
  'quote.pricing-requested': { params: [], detail: 'data' },
  // S-2a (quote/pricing.go): the detail is the client's name, as stored.
  'quote.pricing-ready': { params: [], detail: 'data' },
  'quote.pricing-reopened': { params: [], detail: 'data' },

  // cmd/asa (conversion)
  'quote.converted': { params: ['caseNumber'], detail: 'worded' },

  // finance
  'finance.rate-change-requested': { params: MONEY_PARAMS, detail: 'worded' },
  'finance.rate-change-decided.approved': { params: MONEY_PARAMS, detail: 'worded' },
  'finance.rate-change-decided.rejected': { params: MONEY_PARAMS, detail: 'worded' },
  'case.expense-changed.recorded': { params: ['item', 'amount', 'currency'], detail: 'worded' },
  'case.expense-changed.amended': { params: ['item', 'amount', 'currency'], detail: 'worded' },
  'case.expense-changed.removed': { params: ['item', 'amount', 'currency'], detail: 'worded' },
  'finance.payment-awaits-confirmation': {
    params: ['reference', 'amount', 'currency'],
    detail: 'worded',
  },
}

/** The seven participant roles. A `Record` over the union, so a role added to
 *  the type is a compile error here until it is listed. */
const PARTICIPANT_ROLES: Readonly<Record<ParticipantRole, true>> = {
  shipper: true,
  consignee: true,
  agent: true,
  'counter-agent': true,
  'vehicle-owner': true,
  dispatcher: true,
  driver: true,
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/
const COUNT = /^\d+$/
const DECIMAL = /^-?\d+(\.\d+)?$/
const CURRENCY = /^[A-Z]{3}$/

/* Typed text never passes through i18next.
 *
 * An item, a reference, an author's name is whatever somebody typed, and
 * i18next's interpolator does not treat a value as opaque: it substitutes each
 * `{{slot}}` with `String.replace` on the FIRST occurrence of that slot's text,
 * so an item reading `Тариф {{amount}}` takes the line's amount and leaves the
 * amount's own slot printed raw. (`skipOnVariables`, on by default since
 * i18next 21, stops a value being re-scanned for its own slot, not this.)
 *
 * So a typed value goes in as a private-use token and is put back after `t()`,
 * in ONE pass over the result: nothing put back is read again, not even a typed
 * value that itself holds a token. Values this file produces itself — a status
 * word, a role, a date, an amount, a count — go in as they are. */
const TOKEN = /\uE000(\w+)\uE001/g
const tokenFor = (name: string) => `\uE000${name}\uE001`

function restoreTyped(text: string, typed: Readonly<Record<string, string>>): string {
  return text.replace(TOKEN, (token, name: string) =>
    Object.hasOwn(typed, name) ? (typed[name] ?? token) : token,
  )
}

/** The words for one row. */
export interface NotificationWords {
  title: string
  detail: string
}

/** The values a code's strings are filled from, or undefined when one of them
 *  cannot be named. */
function interpolation(
  message: NotificationMessage,
  params: Readonly<Record<string, unknown>>,
  t: TFunction,
  formatDate: (iso: string) => string,
):
  | { values: Record<string, string | number>; typed: Record<string, string>; bare: boolean }
  | undefined {
  const values: Record<string, string | number> = {}
  const typed: Record<string, string> = {}
  let bare = false
  const currency = params.currency

  for (const name of message.params) {
    const raw = Object.hasOwn(params, name) ? params[name] : undefined
    if (typeof raw !== 'string') return undefined

    switch (PARAM_KINDS[name]) {
      case 'status': {
        const status = statusFromWire(raw)
        if (!status) return undefined
        values[name] = t(`caseStatus.${status}`)
        break
      }
      case 'role': {
        if (!Object.hasOwn(PARTICIPANT_ROLES, raw)) return undefined
        values[name] = t(`participantRole.${raw as ParticipantRole}`)
        break
      }
      case 'date':
        if (!ISO_DATE.test(raw)) return undefined
        values[name] = formatDate(raw)
        break
      case 'count':
        if (!COUNT.test(raw)) return undefined
        values[name] = Number(raw)
        break
      case 'money':
        if (!DECIMAL.test(raw) || typeof currency !== 'string' || !CURRENCY.test(currency)) {
          return undefined
        }
        values[name] = formatMoney(raw, currency)
        break
      case 'currency':
        // Folded into each amount above; never a value of its own.
        break
      case 'text':
        values[name] = tokenFor(name)
        typed[name] = raw
        break
      case 'optionalText':
        if (raw === '') bare = true
        values[name] = tokenFor(name)
        typed[name] = raw
        break
      case undefined:
        // A param name with no kind: the drift test fails on it, and a raw
        // value must not reach a screen meanwhile.
        return undefined
    }
  }
  return { values, typed, bare }
}

/** One bell row in the reader's language, or undefined when its code is
 *  unknown or a value cannot be named — the caller then falls back. */
export function wordNotification(
  item: Pick<Notification, 'code' | 'params' | 'detail'>,
  t: TFunction,
  formatDate: (iso: string) => string,
): NotificationWords | undefined {
  const { code } = item
  if (!code || !Object.hasOwn(NOTIFICATION_MESSAGES, code)) return undefined
  const message = NOTIFICATION_MESSAGES[code]
  // Typed as always an object, but a streamed frame is only checked for its id
  // and href (`isNotificationWire`), so a malformed one is declined here
  // rather than read.
  const params: unknown = item.params
  if (!message || typeof params !== 'object' || params === null) return undefined

  const filled = interpolation(message, params as Record<string, unknown>, t, formatDate)
  if (!filled) return undefined

  /* Built keys, widened to the catalogue's key type: the drift test is what
     holds every code to a `title` in both catalogues, and `detail` to exactly
     the codes whose detail is worded. */
  const base = `common.notifications.messages.${code}`
  const title = restoreTyped(t(`${base}.title` as ParseKeys, filled.values), filled.typed)

  switch (message.detail) {
    case 'data':
      return { title, detail: item.detail }
    case 'none':
      return { title, detail: '' }
    case 'worded':
      return {
        title,
        detail: restoreTyped(
          t(`${base}.detail` as ParseKeys, {
            ...filled.values,
            ...(filled.bare ? { context: 'bare' } : {}),
          }),
          filled.typed,
        ),
      }
  }
}
